import React, { useState, useEffect } from 'react';
import { Row, Col, Button, ButtonGroup, Spinner, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import ProductCard from '../../components/ProductCard';
import SearchBar from '../../components/SearchBar';
import Pagination from '../../components/Pagination';
import { getShopByName, getShopProducts, getShopProductsPaginated, getProductsCount, convertUrlToShopName, getAllProductCategories } from '../../services/shopService';
import { useCart } from '../../contexts/CartContext';
import useDebounce from '../../hooks/useDebounce';

const ShopDetail = () => {
  const { shopName } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [shopData, setShopData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Stati per la paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const { debouncedValue: searchQuery, isDebouncing } = useDebounce(searchInput, 300);

  // useEffect per caricare i dati del negozio e le categorie
  useEffect(() => {
    const loadShopData = async () => {
      try {
        setLoading(true);
        const convertedName = convertUrlToShopName(shopName);
        const shop = await getShopByName(convertedName);
        if (!shop) {
          setError('Negozio non trovato');
          return;
        }
        setShopData(shop);

        // Carica le categorie SOLO dai prodotti esistenti (escludendo disabilitati)
        const productCategories = await getAllProductCategories(shop.id, false);
        setCategories(productCategories);
      } catch (err) {
        setError('Errore nel caricamento dei dati del negozio');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadShopData();
  }, [shopName]);

  // useEffect per caricare i prodotti (dual-mode: ricerca client-side o paginazione server-side)
  useEffect(() => {
    const loadProducts = async () => {
      if (!shopData?.id) return;

      try {
        setLoading(true);

        if (searchQuery.trim()) {
          // SEARCH MODE: carica tutti i prodotti e filtra client-side (vero substring matching)
          const allProducts = await getShopProducts(shopData.id, false);
          const q = searchQuery.toLowerCase();
          let filtered = allProducts.filter(p =>
            p.name.toLowerCase().includes(q)
          );
          // Applica anche il filtro categoria se selezionato
          if (selectedCategory !== 'all') {
            filtered = filtered.filter(p => p.category === selectedCategory);
          }
          setProducts(filtered);
          setTotalPages(1);
          setLastDoc(null);
        } else {
          // NORMAL MODE: paginazione server-side (solo filtro categoria, no ricerca testuale)
          const filters = {
            category: selectedCategory,
            searchQuery: ''
          };

          const [totalCount, { products: newProducts, lastVisible }] = await Promise.all([
            getProductsCount(shopData.id, filters),
            getShopProductsPaginated(
              shopData.id,
              undefined,
              currentPage > 1 ? lastDoc : null,
              filters
            )
          ]);

          setTotalPages(Math.ceil(totalCount / 50));
          setProducts(newProducts.filter(p => !p.disabled));
          setLastDoc(lastVisible);
        }
      } catch (err) {
        setError('Errore nel caricamento dei prodotti');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [shopData?.id, currentPage, selectedCategory, searchQuery]);

  const handleAddToCart = (productWithQuantity) => {
    addToCart({
      ...productWithQuantity,
      shopId: shopData.id
    }, productWithQuantity.quantity);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setSearchInput('');
  };

  // Stili personalizzati per i bottoni
  const primaryButtonStyle = {
    backgroundColor: "#0A0A17",
    borderColor: "#0A0A17"
  };

  const outlineButtonStyle = {
    borderColor: "#0A0A17",
    color: "#0A0A17"
  };

  const renderPagination = () =>
    totalPages > 1 && (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>;
  }

  if (!shopData) {
    return <div className="text-center py-5">Negozio non trovato</div>;
  }
  
  return (
    <div className="py-3">
      {/* Card informazioni negozio */}
      <Card className="mb-3 rounded-4 border-0 shadow-sm">
        <Card.Body className="p-3">
          <Row className="align-items-center">
            <Col xs="auto" className="pe-0">
              <div className="rounded-4 overflow-hidden bg-white" style={{ width: 80, height: 80 }}>
                {shopData.logoUrl ? (
                  <img 
                    src={shopData.logoUrl} 
                    alt={shopData.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="w-100 h-100 d-flex justify-content-center align-items-center bg-light">
                    <i className="bi bi-box text-secondary" style={{ fontSize: '2.5rem' }}></i>
                  </div>
                )}
              </div>
            </Col>
            <Col>
              <div className="d-flex flex-column">
                <h5 className="fw-bold mb-1">{shopData.name}</h5>
                {shopData.subtitle && <p className="text-muted small mb-2">{shopData.subtitle}</p>}
                <div className="d-flex flex-wrap gap-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-geo-alt me-1 text-primary small"></i>
                    <span className="small">{shopData.address?.street}, {shopData.address?.city}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-clock me-1 text-primary small"></i>
                    <span className="small">{shopData.openingHours || 'Orari non disponibili'}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-telephone me-1 text-primary small"></i>
                    <span className="small">{shopData.contact?.phone || 'Non disponibile'}</span>
        </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-wechat me-1 text-primary small"></i>
                    <span className="small">{shopData.contact?.wechatId || 'Non disponibile'}</span>
        </div>
        </div>
      </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Barra di ricerca */}
      <SearchBar
        placeholder={t('shop.search_products')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />
      
      {/* Categorie */}
      <div className="my-2 pb-2 overflow-auto">
        <ButtonGroup className="d-flex flex-nowrap" style={{ minWidth: 'max-content' }}>
          <Button 
            variant={selectedCategory === 'all' ? 'primary' : 'outline-primary'}
            className={`rounded-pill me-2 ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
            style={selectedCategory === 'all' ? primaryButtonStyle : outlineButtonStyle}
          >
            {t('shop.all_categories')}
          </Button>
          
          {categories.map(category => (
            <Button 
              key={category}
              variant={selectedCategory === category ? 'primary' : 'outline-primary'}
              className={`rounded-pill me-2 ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category)}
              style={selectedCategory === category ? primaryButtonStyle : outlineButtonStyle}
            >
              {category}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      
      {/* Griglia prodotti */}
      <Row xs={2} md={3} className="g-2">
        {products.map(product => (
          <Col key={product.id}>
            <ProductCard
              product={product}
              onAddToCart={handleAddToCart}
              onClick={() => navigate(`/${shopName}/product/${product.id}`)}
            />
          </Col>
        ))}
      </Row>

      {/* Paginazione inferiore */}
      {renderPagination()}

      {/* Messaggio se non ci sono prodotti */}
      {products.length === 0 && !loading && (
        <div className="text-center py-5">
          <p className="text-muted">
            {searchQuery 
              ? t('shop.no_products_found_search')
              : t('shop.no_products_found_category')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ShopDetail; 